import { useSaveData } from '@entities/common/savedData/index.ts';
import { useCallback, useEffect, useMemo } from 'react';
import { Partition, Tag } from '@src/api/common.ts';

export const usePartitionsWithTagsSelection = (props: {
  hash?: string;
  initializeData?: {
    savedFormState?: unknown;
    tags: Tag[];
    partitions: Partition[];
  };
}) => {
  const { initializeData, hash } = props;
  const { data, doSetStorageData, doRemoveStorageData } = useSaveData<Map<number, number[]>>(
    `usePartitionsWithTagsSelection_${hash}`
  );

  const addPartitionWithTags = useCallback(
    ({ tagsIds, partitionId }: { partitionId: number; tagsIds: number[] }) => {
      const state = new Map(data?.entries());
      state.set(partitionId, tagsIds);
      doSetStorageData(state);
    },
    [data, doSetStorageData]
  );

  const removePartition = useCallback(
    ({ partitionId }: { partitionId: number }) => {
      const state = new Map(data?.entries());
      state.delete(partitionId);
      doSetStorageData(state);
    },
    [data, doSetStorageData]
  );

  const addPartitions = useCallback(
    ({ partitionsIds }: { partitionsIds: number[] }) => {
      const state = new Map();

      partitionsIds.forEach((id) => {
        const includedTags = data?.get(id);
        state.set(id, includedTags?.length ? includedTags : []);
      });
      doSetStorageData(state);
    },
    [data, doSetStorageData]
  );

  const selectedTags = useMemo(() => {
    const output: number[] = [];
    data?.forEach((values) => {
      output.push(...values);
    });
    return output;
  }, [data]);

  const selectedPartitions = useMemo(() => {
    const output: number[] = [];
    data?.forEach((_values, key) => {
      output.push(key);
    });
    return output;
  }, [data]);

  useEffect(() => {
    if (initializeData) {
      const savedFormState = initializeData?.savedFormState;
      const tags = initializeData?.tags;
      const partitions = initializeData?.partitions;
      if (
        typeof savedFormState === 'undefined' &&
        !selectedPartitions.length &&
        partitions.length
      ) {
        const state = new Map();
        partitions.forEach((partition) => {
          state.set(
            partition.id,
            tags?.filter((tag) => tag.partition === partition.id).map((tag) => tag.id) || []
          );
        });
        doSetStorageData(state);
      }
    }
  }, [addPartitionWithTags, initializeData, selectedPartitions, doSetStorageData]);

  return {
    partitionsMap: data ?? new Map(),
    addPartitionWithTags,
    addPartitions,
    removePartition,
    selectedTags,
    selectedPartitions,
    clearPartitionsData: doRemoveStorageData,
  };
};
